import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { User, Session } from '@supabase/supabase-js'
import { AuthService, LoginCredentials, SignUpCredentials, UserProfile } from '../../../services/AuthService'

export interface AuthError {
  message: string
  code?: string
  details?: string
}

export interface AuthState {
  user: User | null
  profile: UserProfile | null // Agregado
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: AuthError | null
  isInitialized: boolean
}

const initialState: AuthState = {
  user: null,
  profile: null, // Agregado
  session: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
}

const authService = AuthService.getInstance()

// Helper function to format errors
const formatAuthError = (error: any): AuthError => {
  // Handle Supabase auth errors
  if (error?.message) {
    const code = error.code || error.error_code || 'unknown_error'
    
    switch (code) {
      case 'email_not_confirmed':
        return {
          message: 'Please check your email and click the confirmation link to activate your account.',
          code,
          details: 'Your account needs to be verified before you can sign in.'
        }
      case 'invalid_credentials':
        return {
          message: 'Invalid email or password. Please check your credentials and try again.',
          code,
          details: 'The email or password you entered is incorrect.'
        }
      case 'email_address_invalid':
        return {
          message: 'Please enter a valid email address.',
          code,
          details: 'The email format is not valid.'
        }
      case 'weak_password':
        return {
          message: 'Password is too weak. Please choose a stronger password.',
          code,
          details: 'Password must be at least 8 characters with uppercase, lowercase, and numbers.'
        }
      case 'signup_disabled':
        return {
          message: 'New registrations are currently disabled.',
          code,
          details: 'Please contact support for assistance.'
        }
      case 'email_address_not_authorized':
        return {
          message: 'This email is not authorized to sign up.',
          code,
          details: 'Please use an authorized email address or contact support.'
        }
      case 'too_many_requests':
        return {
          message: 'Too many attempts. Please wait a moment before trying again.',
          code,
          details: 'Rate limit exceeded. Please try again in a few minutes.'
        }
      case 'user_not_found':
        return {
          message: 'No account found with this email address.',
          code,
          details: 'Please check your email or create a new account.'
        }
      default:
        return {
          message: error.message || 'An unexpected error occurred. Please try again.',
          code,
          details: error.hint || 'If this problem persists, please contact support.'
        }
    }
  }
  
  // Handle network errors
  if (error?.name === 'NetworkError' || error?.code === 'NETWORK_ERROR') {
    return {
      message: 'Network connection error. Please check your internet connection.',
      code: 'network_error',
      details: 'Unable to connect to the authentication service.'
    }
  }
  
  // Handle unknown errors
  return {
    message: 'An unexpected error occurred. Please try again.',
    code: 'unknown_error',
    details: typeof error === 'string' ? error : JSON.stringify(error)
  }
}

// Async thunks with improved error handling
export const signIn = createAsyncThunk(
  'auth/signIn',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.signIn(credentials);
      if (response.error) {
        return rejectWithValue(formatAuthError(response.error))
      }
      return response
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

export const signUp = createAsyncThunk(
  'auth/signUp',
  async (credentials: SignUpCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.signUp(credentials)
      if (response.error) {
        return rejectWithValue(formatAuthError(response.error))
      }
      return response
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.signOut()
      if (response.error) {
        return rejectWithValue(formatAuthError(response.error))
      }
      return response
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (uri: string | undefined, { rejectWithValue }) => {
    try {
      const response = await authService.signInWithGoogle(uri)
      if (response.error) {
        return rejectWithValue(formatAuthError(response.error))
      }
      return response
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const session = await authService.getCurrentSession()
      
      if (session?.user) {
        const { user, profile } = await authService.getCurrentUserWithProfile()
        return { user, profile, session }
      }
      
      return { user: null, profile: null, session: null }
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(email)
      if (response.error) {
        return rejectWithValue(formatAuthError(response.error))
      }
      return response
    } catch (error) {
      return rejectWithValue(formatAuthError(error))
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<{ user: User | null; session: Session | null; profile?: UserProfile | null }>) => {
      state.user = action.payload.user
      state.session = action.payload.session
      state.profile = action.payload.profile || null // Agregado
      state.isAuthenticated = !!action.payload.user
      state.error = null
      state.isInitialized = true
    },
    updateProfile: (state, action: PayloadAction<UserProfile>) => { // Agregado
      state.profile = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<AuthError>) => {
      state.error = action.payload
      state.isLoading = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign In
      .addCase(signIn.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.session = action.payload.session
        state.isAuthenticated = !!action.payload.user
        state.error = null
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as AuthError
      })
      
      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.session = action.payload.session
        state.isAuthenticated = !!action.payload.user
        state.error = null
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as AuthError
      })
      
      // Sign Out
      .addCase(signOut.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signOut.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.session = null
        state.isAuthenticated = false
        state.error = null
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as AuthError
      })
      
      // Google Sign In
      .addCase(signInWithGoogle.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signInWithGoogle.fulfilled, (state) => {
        state.isLoading = false
        // OAuth redirect handled separately
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as AuthError
      })
      
      // Initialize Auth - actualizado
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.profile = action.payload.profile // Agregado
        state.session = action.payload.session
        state.isAuthenticated = !!action.payload.user
        state.isInitialized = true
        state.isLoading = false
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isInitialized = true
        state.isLoading = false
        state.error = action.payload as AuthError
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as AuthError
      })
  },
})

export const { setAuthState, updateProfile, clearError, setLoading, setError } = authSlice.actions
export default authSlice.reducer